import { Button } from "@arco-design/web-react";
import { getAuth, OAuthProvider, signInWithPopup } from "firebase/auth";
import { endpoints } from "../api/endpoints";
import { resetCachedToken } from "../setupAxios";
import axios from "axios";
import { routes } from "../routes/routes";
import { Message } from "@arco-design/web-react";
import { WindowsOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux";
import { clearUser, setUser } from "../store/userSlice";
import { RootState } from "../store/store";
import { defaultAuthenticatedPath } from "../utils/defaultAuthenticatedPath";
import { isAuthPopupClosedByUser } from "../utils/isAuthPopupClosedByUser";
import { FirebaseError } from "firebase/app";
import { useTranslation } from "react-i18next";
import { getOAuthButtonProps } from "../utils/oauthButtonStyle";
import { completeAuthRedirect } from "../utils/authReturnUrl";

type MicrosoftLoginButtonProps = {
    variant?: "default" | "icon";
};

export const MicrosoftLoginButton = ({ variant = "default" }: MicrosoftLoginButtonProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const eventIntakeEnabled = useSelector(
        (state: RootState) => state.config.configLoaded && state.config.eventIntakeEnabled,
    );

    const handleMicrosoftLogin = async () => {
        const auth = getAuth();
        const provider = new OAuthProvider("microsoft.com");

        try {
            resetCachedToken();
            dispatch(clearUser());

            const result = await signInWithPopup(auth, provider);

            if(result) {
                const firebaseUser = result.user;
                const idToken = await firebaseUser.getIdToken();
                const response = await axios.get(endpoints.whoami, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });

                dispatch(setUser({ ...response.data }));
                completeAuthRedirect(
                    defaultAuthenticatedPath(
                        response.data,
                        eventIntakeEnabled,
                        new URLSearchParams(window.location.search),
                    ),
                );
            }
        } catch (error) {
            console.error("Microsoft login failed:", error);
            if (isAuthPopupClosedByUser(error)) {
                return;
            } 

            if (error instanceof FirebaseError && error.code === "auth/account-exists-with-different-credential") {
                Message.error(t('notification_messages.email_exists_with_google_error')
                );
                return;
            }

            Message.error(t('notification_messages.email_not_found_error'));
        }
    };

    if (variant === "icon") {
        return (
            <Button
                className="auth-sso-btn"
                onClick={handleMicrosoftLogin}
                icon={<WindowsOutlined />}
                aria-label={t('login_page.microsoft_login')}
            />
        );
    }

    return (
        <Button
            onClick={handleMicrosoftLogin}
            shape="round"
            size="large"
            icon={<WindowsOutlined />}
            {...getOAuthButtonProps()}
        >
            {t('login_page.microsoft_login')}
        </Button>
    );
};
